/**
 * TajerGo Platform Admin phone UI kit (< md only). See docs/design/mobile-app-ui-platform-admin.md.
 * Presentational only: callers pass the SAME handlers/state the md+ tree uses.
 * Tokens: teal #1FB7A6 = action, navy #0F2A44 = structure, #146F66 = teal text
 * on white (AA), muted #64748B. Logical classes only (RTL via <html dir>).
 */
import { useId, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    ArrowLeft,
    CalendarDays,
    ChevronRight,
    ChevronUp,
    Download,
    FileSpreadsheet,
    Minus,
    Plus,
    Printer,
    Search,
    Share2,
} from 'lucide-react';
import { cn } from '@/lib/cn';

type IconType = LucideIcon;

const focusRing = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1FB7A6] focus-visible:ring-offset-2';
const tealPill = `inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-[#1FB7A6] px-5 text-[15px] font-semibold text-white shadow-sm shadow-[#1FB7A6]/25 transition-colors hover:bg-[#189d90] disabled:opacity-50 ${focusRing}`;
const outlinePill = `inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-[#0F2A44]/15 bg-white px-5 text-[15px] font-semibold text-[#0F2A44] transition-colors hover:bg-[#0F2A44]/[0.04] disabled:opacity-50 ${focusRing}`;

/* ─────────────────────────── Screen + header ─────────────────────────── */

export interface MobileScreenProps {
    children: ReactNode;
    /** Extra bottom room: 'tabbar' (default) clears the floating tab bar;
     * 'summary' clears tab bar + SummaryBar; 'action' clears a StickyActionBar
     * (screens that hide the tab bar); 'none' for full-bleed. */
    bottom?: 'tabbar' | 'summary' | 'action' | 'none';
    className?: string;
}

/** Soft tinted phone canvas. The shell already clears the tab bar; this adds room for extra bars. */
export function MobileScreen({ children, bottom = 'tabbar', className }: MobileScreenProps) {
    return (
        <div
            data-testid="mobile-screen"
            className={cn(
                '-mx-4 -mt-4 min-h-full bg-[var(--m-canvas)] px-4 pt-3',
                bottom === 'summary' && 'pb-[calc(var(--m-summary-bar-h)+1rem)]',
                bottom === 'action' && 'pb-[calc(var(--m-action-bar-h)+1rem)]',
                bottom === 'tabbar' && 'pb-4',
                className,
            )}
        >
            <div className="space-y-3">{children}</div>
        </div>
    );
}

export interface MobileHeaderProps {
    title: ReactNode;
    /** Back target: a path (renders a Link) or a handler. Omit on root tab screens. */
    back?: string | (() => void);
    /** Root tab screens: greeting/eyebrow shown instead of the back button. */
    greeting?: ReactNode;
    /** One end-side action: an ActionPill or IconCircleButton. */
    action?: ReactNode;
    className?: string;
}

export function MobileHeader({ title, back, greeting, action, className }: MobileHeaderProps) {
    const { t } = useTranslation();
    const backLabel = t('mobile.back', 'Back');
    const backClass = `grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white text-[#0F2A44] shadow-sm ${focusRing}`;
    const backIcon = <ArrowLeft className="h-5 w-5 rtl:rotate-180" aria-hidden="true" />;
    if (!back && greeting !== undefined) {
        return (
            <header className={cn('flex min-h-12 items-center gap-3', className)}>
                <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] text-[#64748B]">{greeting}</p>
                    <h1 className="truncate text-xl font-bold text-[#0F2A44]">{title}</h1>
                </div>
                {action}
            </header>
        );
    }
    return (
        <header className={cn('grid min-h-12 grid-cols-[2.75rem_1fr_auto] items-center gap-2', className)}>
            {typeof back === 'string' ? (
                <Link to={back} aria-label={backLabel} className={backClass}>{backIcon}</Link>
            ) : back ? (
                <button type="button" onClick={back} aria-label={backLabel} className={backClass}>{backIcon}</button>
            ) : (
                <span aria-hidden="true" />
            )}
            <h1 className="truncate text-center text-[17px] font-semibold text-[#0F2A44]">{title}</h1>
            <div className="flex min-w-11 justify-end">{action}</div>
        </header>
    );
}

export interface ActionPillProps {
    children: ReactNode;
    onClick?: () => void;
    to?: string;
    icon?: IconType;
    variant?: 'primary' | 'secondary' | 'outline';
    disabled?: boolean;
    type?: 'button' | 'submit';
    className?: string;
    'aria-label'?: string;
}

/** Text pill button ("Add Item", "Change", "Next"). */
export function ActionPill({ children, onClick, to, icon: Icon, variant = 'secondary', disabled, type = 'button', className, ...rest }: ActionPillProps) {
    const cls = cn(
        variant === 'primary' ? tealPill : variant === 'outline' ? outlinePill
            : `inline-flex min-h-11 items-center justify-center gap-1.5 rounded-full bg-[#1FB7A6]/10 px-4 text-[14px] font-semibold text-[#146F66] transition-colors hover:bg-[#1FB7A6]/15 disabled:opacity-50 ${focusRing}`,
        className,
    );
    const content = <>{Icon ? <Icon className="h-4 w-4" aria-hidden="true" /> : null}{children}</>;
    if (to && !disabled) return <Link to={to} className={cls} aria-label={rest['aria-label']}>{content}</Link>;
    return <button type={type} onClick={onClick} disabled={disabled} className={cls} aria-label={rest['aria-label']}>{content}</button>;
}

export interface IconCircleButtonProps {
    icon: IconType;
    label: string;
    onClick?: () => void;
    to?: string;
    tone?: 'light' | 'teal' | 'danger';
    size?: 'md' | 'lg';
    disabled?: boolean;
    className?: string;
}

/** Icon-only circle, always with an aria-label. 44px (md) or 56px (lg). */
export function IconCircleButton({ icon: Icon, label, onClick, to, tone = 'light', size = 'md', disabled, className }: IconCircleButtonProps) {
    const cls = cn(
        'grid shrink-0 place-items-center rounded-full transition-colors disabled:opacity-50',
        focusRing,
        size === 'lg' ? 'h-14 w-14' : 'h-11 w-11',
        tone === 'teal' && 'bg-[#1FB7A6] text-white shadow-md shadow-[#1FB7A6]/30 hover:bg-[#189d90]',
        tone === 'light' && 'bg-white text-[#0F2A44] shadow-sm hover:bg-[#F4F6F9]',
        tone === 'danger' && 'bg-[#EF4444]/10 text-[#B91C1C] hover:bg-[#EF4444]/15',
        className,
    );
    const icon = <Icon className={size === 'lg' ? 'h-6 w-6' : 'h-5 w-5'} aria-hidden="true" />;
    if (to && !disabled) return <Link to={to} aria-label={label} className={cls}>{icon}</Link>;
    return <button type="button" onClick={onClick} disabled={disabled} aria-label={label} className={cls}>{icon}</button>;
}

/* ─────────────────────────── Inputs ─────────────────────────── */

export interface SearchPillProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    /** Accessible label; defaults to placeholder. */
    label?: string;
    /** Optional end button (scan / voice / filter). */
    endAction?: { icon: IconType; label: string; onClick: () => void; active?: boolean };
    onSubmit?: () => void;
    autoFocus?: boolean;
    className?: string;
}

export function SearchPill({ value, onChange, placeholder, label, endAction, onSubmit, autoFocus, className }: SearchPillProps) {
    const { t } = useTranslation();
    const ph = placeholder ?? t('mobile.search', 'Search');
    const End = endAction?.icon;
    return (
        <form
            role="search"
            onSubmit={(event) => { event.preventDefault(); onSubmit?.(); }}
            className={cn('flex h-12 items-center gap-2 rounded-full bg-white ps-4 pe-1 shadow-sm ring-1 ring-[#0F2A44]/[0.06] focus-within:ring-2 focus-within:ring-[#1FB7A6]', className)}
        >
            <Search className="h-5 w-5 shrink-0 text-[#64748B]" aria-hidden="true" />
            <input
                type="search"
                value={value}
                onChange={(event) => onChange(event.target.value)}
                placeholder={ph}
                aria-label={label ?? ph}
                autoFocus={autoFocus}
                // 16px stops iOS zoom-on-focus.
                className="h-full min-w-0 flex-1 bg-transparent text-base text-[#0F2A44] outline-none placeholder:text-[#94A3B8] [&::-webkit-search-cancel-button]:hidden"
            />
            {End ? (
                <button
                    type="button"
                    onClick={endAction!.onClick}
                    aria-label={endAction!.label}
                    aria-pressed={endAction!.active}
                    className={cn('grid h-10 w-10 shrink-0 place-items-center rounded-full', focusRing, endAction!.active ? 'bg-[#1FB7A6] text-white' : 'bg-[#1FB7A6]/10 text-[#146F66]')}
                >
                    <End className="h-5 w-5" aria-hidden="true" />
                </button>
            ) : null}
        </form>
    );
}

export interface ChipTabOption<V extends string = string> {
    value: V;
    label: ReactNode;
    count?: number;
}

export interface ChipTabsProps<V extends string = string> {
    options: ChipTabOption<V>[];
    value: V;
    onChange: (value: V) => void;
    /** Accessible name for the tablist. */
    label: string;
    className?: string;
}

/** Horizontally scrolling chip row (role=tablist, aria-selected). */
export function ChipTabs<V extends string = string>({ options, value, onChange, label, className }: ChipTabsProps<V>) {
    return (
        <div role="tablist" aria-label={label} className={cn('-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden', className)}>
            {options.map((option) => {
                const selected = option.value === value;
                return (
                    <button
                        key={option.value}
                        type="button"
                        role="tab"
                        aria-selected={selected}
                        onClick={() => onChange(option.value)}
                        className={cn(
                            'inline-flex min-h-11 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-4 text-[14px] font-medium transition-colors',
                            focusRing,
                            selected ? 'bg-[#1FB7A6]/15 text-[#146F66] ring-1 ring-[#1FB7A6]/40' : 'bg-white text-[#0F2A44] ring-1 ring-[#0F2A44]/[0.08]',
                        )}
                    >
                        {option.label}
                        {option.count !== undefined ? (
                            <span className="tabular-nums text-[12px] text-[#64748B]">{option.count}</span>
                        ) : null}
                    </button>
                );
            })}
        </div>
    );
}

/* ─────────────────────────── Cards + rows ─────────────────────────── */

export interface MobileCardProps {
    children: ReactNode;
    title?: ReactNode;
    /** Trailing header element (e.g. "Change" pill). */
    action?: ReactNode;
    /** 'none' for cards holding ListRows edge-to-edge. */
    padding?: 'md' | 'none';
    className?: string;
}

export function MobileCard({ children, title, action, padding = 'md', className }: MobileCardProps) {
    return (
        <section className={cn('rounded-[18px] bg-white shadow-[0_1px_3px_rgba(15,42,68,0.06),0_4px_16px_rgba(15,42,68,0.04)]', padding === 'md' && 'p-4', className)}>
            {title || action ? (
                <div className={cn('mb-2 flex items-center gap-2', padding === 'none' && 'px-4 pt-4')}>
                    {title ? <h2 className="min-w-0 flex-1 truncate text-[15px] font-semibold text-[#0F2A44]">{title}</h2> : <span className="flex-1" />}
                    {action}
                </div>
            ) : null}
            {children}
        </section>
    );
}

export interface ListRowProps {
    title: ReactNode;
    /** Small type label above the title (e.g. "Sale #1"). */
    eyebrow?: ReactNode;
    /** Muted line under the title (date · time). */
    meta?: ReactNode;
    leadingIcon?: IconType;
    /** Initials / image avatar instead of an icon. */
    avatar?: ReactNode;
    /** End-aligned value, e.g. an AED amount (render it dir="ltr" tabular-nums). */
    value?: ReactNode;
    badge?: ReactNode;
    chevron?: boolean;
    to?: string;
    onClick?: () => void;
    className?: string;
}

/** Row for lists: renders as Link (to), button (onClick) or plain div. */
export function ListRow({ title, eyebrow, meta, leadingIcon: Icon, avatar, value, badge, chevron, to, onClick, className }: ListRowProps) {
    const cls = cn('flex min-h-14 w-full min-w-0 items-center gap-3 px-4 py-3 text-start', (to || onClick) && `transition-colors hover:bg-[#0F2A44]/[0.03] ${focusRing} focus-visible:ring-inset focus-visible:ring-offset-0`, className);
    const body = (
        <>
            {avatar ?? (Icon ? (
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#1FB7A6]/10 text-[#146F66]">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
            ) : null)}
            <span className="min-w-0 flex-1">
                {/* title= keeps the full value (emails, IDs, IPs) readable when truncated. */}
                {eyebrow ? <span className="block truncate text-[12px] font-medium text-[#64748B]" title={typeof eyebrow === 'string' ? eyebrow : undefined}>{eyebrow}</span> : null}
                <span className="block truncate text-[15px] font-medium text-[#0F2A44]" title={typeof title === 'string' ? title : undefined}>{title}</span>
                {meta ? <span className="block truncate text-[13px] text-[#64748B]" title={typeof meta === 'string' ? meta : undefined}>{meta}</span> : null}
            </span>
            {value !== undefined || badge ? (
                <span className="flex shrink-0 flex-col items-end gap-1">
                    {value !== undefined ? <span className="text-[15px] font-semibold tabular-nums text-[#0F2A44]" dir="ltr">{value}</span> : null}
                    {badge}
                </span>
            ) : null}
            {chevron ? <ChevronRight className="h-5 w-5 shrink-0 text-[#94A3B8] rtl:rotate-180" aria-hidden="true" /> : null}
        </>
    );
    if (to) return <Link to={to} className={cls}>{body}</Link>;
    if (onClick) return <button type="button" onClick={onClick} className={cls}>{body}</button>;
    return <div className={cls}>{body}</div>;
}

/** Divided list inside a MobileCard padding="none". */
export function ListGroup({ children, className }: { children: ReactNode; className?: string }) {
    return <div className={cn('divide-y divide-[#0F2A44]/[0.06] overflow-hidden rounded-[18px]', className)}>{children}</div>;
}

export type StatusTone = 'paid' | 'unpaid' | 'pending' | 'neutral';

export interface StatusBadgeProps {
    tone: StatusTone;
    /** Visible text is mandatory — colour is never the only signal. */
    children: ReactNode;
    className?: string;
}

export function StatusBadge({ tone, children, className }: StatusBadgeProps) {
    return (
        <span
            data-tone={tone}
            className={cn(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[12px] font-semibold',
                tone === 'paid' && 'bg-[#10B981]/12 text-[#047857]',
                tone === 'unpaid' && 'bg-[#EF4444]/10 text-[#B91C1C]',
                tone === 'pending' && 'bg-[#F59E0B]/15 text-[#92400E]',
                tone === 'neutral' && 'bg-[#0F2A44]/[0.06] text-[#475569]',
                className,
            )}
        >
            <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-current" />
            {children}
        </span>
    );
}

/* ─────────────────────────── Hub tiles + KPIs ─────────────────────────── */

export interface TileGridProps {
    title?: ReactNode;
    children: ReactNode;
    columns?: 3 | 4;
    className?: string;
}

export function TileGrid({ title, children, columns = 3, className }: TileGridProps) {
    const id = useId();
    return (
        <section aria-labelledby={title ? id : undefined} className={className}>
            {title ? <h2 id={id} className="mb-2 px-1 text-[15px] font-semibold text-[#0F2A44]">{title}</h2> : null}
            <div className={cn('grid gap-2.5', columns === 4 ? 'grid-cols-4' : 'grid-cols-3')}>{children}</div>
        </section>
    );
}

export interface TileProps {
    icon: IconType;
    label: ReactNode;
    to?: string;
    onClick?: () => void;
    selected?: boolean;
    /** Plan-locked: shows a lock hint; caller's onClick opens the upgrade dialog. */
    locked?: boolean;
    badge?: ReactNode;
    'aria-label'?: string;
}

export function Tile({ icon: Icon, label, to, onClick, selected, locked, badge, ...rest }: TileProps) {
    const cls = cn(
        'relative flex aspect-square min-h-[5.5rem] flex-col items-center justify-center gap-2 rounded-2xl bg-white p-2 text-center shadow-sm transition-colors',
        focusRing,
        selected ? 'ring-2 ring-[#1FB7A6]' : 'ring-1 ring-[#0F2A44]/[0.06] hover:ring-[#1FB7A6]/40',
        locked && 'opacity-70',
    );
    const body = (
        <>
            <span className="grid h-11 w-11 place-items-center rounded-full bg-[#1FB7A6]/10 text-[#146F66]">
                <Icon className="h-5 w-5" aria-hidden="true" />
            </span>
            <span className="line-clamp-2 text-[12px] font-medium leading-tight text-[#0F2A44]">{label}</span>
            {badge ? <span className="absolute end-2 top-2">{badge}</span> : null}
        </>
    );
    if (to && !locked) return <Link to={to} className={cls} aria-current={selected ? 'page' : undefined} aria-label={rest['aria-label']}>{body}</Link>;
    return <button type="button" onClick={onClick} className={cls} aria-label={rest['aria-label']}>{body}</button>;
}

export interface KpiTileProps {
    icon?: IconType;
    label: ReactNode;
    /** Pre-formatted value (e.g. formatCurrency). Rendered dir="ltr" tabular-nums. */
    value: ReactNode;
    /** e.g. "+12% vs last month". */
    comparison?: ReactNode;
    trend?: 'up' | 'down' | 'flat';
    onClick?: () => void;
}

export function KpiTile({ icon: Icon, label, value, comparison, trend, onClick }: KpiTileProps) {
    const body = (
        <>
            <span className="flex items-center gap-2 text-[13px] text-[#64748B]">
                {Icon ? <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#1FB7A6]/10 text-[#146F66]"><Icon className="h-4 w-4" aria-hidden="true" /></span> : null}
                <span className="min-w-0 truncate">{label}</span>
            </span>
            <span className="mt-2 block truncate text-xl font-bold tabular-nums text-[#0F2A44]" dir="ltr">{value}</span>
            {comparison ? (
                <span className={cn('mt-0.5 block truncate text-[12px]', trend === 'up' ? 'text-[#047857]' : trend === 'down' ? 'text-[#B91C1C]' : 'text-[#64748B]')}>{comparison}</span>
            ) : null}
        </>
    );
    const cls = 'block w-full rounded-[18px] bg-white p-3.5 text-start shadow-sm ring-1 ring-[#0F2A44]/[0.04]';
    return onClick
        ? <button type="button" onClick={onClick} className={cn(cls, focusRing)}>{body}</button>
        : <div className={cls}>{body}</div>;
}

/** 2-column KPI grid. */
export function KpiGrid({ children }: { children: ReactNode }) {
    return <div className="grid grid-cols-2 gap-2.5">{children}</div>;
}

export interface DateRangeCardProps {
    /** Current range label ("All Time", "1–30 Sep"). */
    label: ReactNode;
    caption?: ReactNode;
    onChange: () => void;
    changeLabel?: string;
}

export function DateRangeCard({ label, caption, onChange, changeLabel }: DateRangeCardProps) {
    const { t } = useTranslation();
    return (
        <div className="flex items-center gap-3 rounded-[18px] bg-white p-3 shadow-sm">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#1FB7A6]/10 text-[#146F66]">
                <CalendarDays className="h-5 w-5" aria-hidden="true" />
            </span>
            <span className="min-w-0 flex-1">
                {caption ? <span className="block text-[12px] text-[#64748B]">{caption}</span> : null}
                <span className="block truncate text-[15px] font-semibold text-[#0F2A44]">{label}</span>
            </span>
            <ActionPill onClick={onChange}>{changeLabel ?? t('mobile.change', 'Change')}</ActionPill>
        </div>
    );
}

/** "Change" selection row for form screens (customer, supplier, branch). */
export function SelectionRow({ label, value, onChange, changeLabel }: { label: ReactNode; value: ReactNode; onChange: () => void; changeLabel?: string }) {
    const { t } = useTranslation();
    return (
        <div className="flex items-center gap-3">
            <span className="min-w-0 flex-1">
                <span className="block text-[12px] text-[#64748B]">{label}</span>
                <span className="block truncate text-[15px] font-medium text-[#0F2A44]">{value}</span>
            </span>
            <ActionPill onClick={onChange}>{changeLabel ?? t('mobile.change', 'Change')}</ActionPill>
        </div>
    );
}

/** Light teal "+ Add Discount" link-chip. */
export function AddLinkChip({ children, onClick }: { children: ReactNode; onClick: () => void }) {
    return (
        <button type="button" onClick={onClick} className={`inline-flex min-h-11 items-center gap-1.5 rounded-full bg-[#1FB7A6]/10 px-4 text-[14px] font-semibold text-[#146F66] hover:bg-[#1FB7A6]/15 ${focusRing}`}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            {children}
        </button>
    );
}

/** −/+ quantity stepper. */
export function QtyStepper({ value, onDecrement, onIncrement, label, min = 0 }: { value: number; onDecrement: () => void; onIncrement: () => void; label: string; min?: number }) {
    const { t } = useTranslation();
    return (
        <div className="flex items-center gap-1" role="group" aria-label={label}>
            <IconCircleButton icon={Minus} label={t('mobile.decrease', 'Decrease')} onClick={onDecrement} disabled={value <= min} />
            <span className="min-w-8 text-center text-[15px] font-semibold tabular-nums" aria-live="polite" dir="ltr">{value}</span>
            <IconCircleButton icon={Plus} label={t('mobile.increase', 'Increase')} onClick={onIncrement} tone="teal" />
        </div>
    );
}

/* ─────────────────────────── Bottom bars ─────────────────────────── */

export interface StickyActionBarProps {
    primaryLabel: ReactNode;
    onPrimary?: () => void;
    primaryDisabled?: boolean;
    primaryLoading?: boolean;
    /** Submit a form by id instead of onPrimary. */
    primaryForm?: string;
    secondaryLabel?: ReactNode;
    onSecondary?: () => void;
    /** 'screen' sits above the tab bar; 'flush' pins to the viewport bottom (tab bar hidden / inside sheets). */
    position?: 'screen' | 'flush';
}

/** Cancel (outline) + Save (teal) bar. Pair with MobileScreen bottom="action". */
export function StickyActionBar({ primaryLabel, onPrimary, primaryDisabled, primaryLoading, primaryForm, secondaryLabel, onSecondary, position = 'screen' }: StickyActionBarProps) {
    return (
        <div
            data-testid="mobile-sticky-action-bar"
            className={cn(
                'fixed inset-x-0 z-30 flex gap-3 border-t border-[#0F2A44]/[0.06] bg-white/95 px-4 pt-3 backdrop-blur md:hidden',
                position === 'screen' ? 'bottom-[var(--m-tab-bar-h)] pb-3' : 'bottom-0 pb-[calc(0.75rem+env(safe-area-inset-bottom))]',
            )}
        >
            {secondaryLabel ? (
                <button type="button" onClick={onSecondary} className={cn(outlinePill, 'flex-1')}>{secondaryLabel}</button>
            ) : null}
            <button
                type={primaryForm ? 'submit' : 'button'}
                form={primaryForm}
                onClick={onPrimary}
                disabled={primaryDisabled || primaryLoading}
                aria-busy={primaryLoading || undefined}
                className={cn(tealPill, 'flex-1')}
            >
                {primaryLabel}
            </button>
        </div>
    );
}

export interface FabRowProps {
    secondary?: { label: ReactNode; onClick?: () => void; to?: string };
    /** Round "+" in the middle. */
    add?: { label: string; onClick?: () => void; to?: string };
    primary?: { label: ReactNode; onClick?: () => void; to?: string; icon?: IconType };
}

/** Floating "Payment In · + · New Sale" row above the tab bar. Hide entries the user can't use. */
export function FabRow({ secondary, add, primary }: FabRowProps) {
    return (
        <div data-testid="mobile-fab-row" className="pointer-events-none fixed inset-x-0 bottom-[calc(var(--m-tab-bar-h)+0.5rem)] z-30 flex items-center justify-center gap-3 px-4 md:hidden">
            {secondary ? <span className="pointer-events-auto"><ActionPill variant="outline" onClick={secondary.onClick} to={secondary.to} className="shadow-md">{secondary.label}</ActionPill></span> : null}
            {add ? <span className="pointer-events-auto"><IconCircleButton icon={Plus} label={add.label} onClick={add.onClick} to={add.to} tone="teal" size="lg" /></span> : null}
            {primary ? <span className="pointer-events-auto"><ActionPill variant="primary" onClick={primary.onClick} to={primary.to} icon={primary.icon} className="shadow-md">{primary.label}</ActionPill></span> : null}
        </div>
    );
}

export interface SummaryBarProps {
    /** e.g. "3 Items". */
    countLabel: ReactNode;
    /** Pre-formatted total (rendered dir="ltr"). */
    total: ReactNode;
    onExpand?: () => void;
    expandLabel?: string;
    nextLabel?: ReactNode;
    onNext: () => void;
    nextDisabled?: boolean;
}

/** Sticky items/total + Next bar above the tab bar. Pair with MobileScreen bottom="summary". */
export function SummaryBar({ countLabel, total, onExpand, expandLabel, nextLabel, onNext, nextDisabled }: SummaryBarProps) {
    const { t } = useTranslation();
    return (
        <div data-testid="mobile-summary-bar" className="fixed inset-x-3 bottom-[calc(var(--m-tab-bar-h)+0.25rem)] z-30 flex h-[var(--m-summary-bar-h)] items-center gap-3 rounded-[20px] bg-white px-3 shadow-[0_8px_30px_rgba(15,42,68,0.14)] md:hidden">
            {onExpand ? (
                <IconCircleButton icon={ChevronUp} label={expandLabel ?? t('mobile.viewItems', 'View items')} onClick={onExpand} />
            ) : null}
            <span className="min-w-0 flex-1">
                <span className="block text-[12px] text-[#64748B]">{countLabel}</span>
                <span className="block truncate text-lg font-bold tabular-nums text-[#0F2A44]" dir="ltr">{total}</span>
            </span>
            <button type="button" onClick={onNext} disabled={nextDisabled} className={cn(tealPill, 'px-7')}>
                {nextLabel ?? t('mobile.next', 'Next')}
            </button>
        </div>
    );
}

/* ─────────────────────────── Settings ─────────────────────────── */

export interface SettingsGroupProps {
    title?: ReactNode;
    children: ReactNode;
    className?: string;
}

export function SettingsGroup({ title, children, className }: SettingsGroupProps) {
    const id = useId();
    return (
        <section aria-labelledby={title ? id : undefined} className={className}>
            {title ? <h2 id={id} className="mb-1.5 px-2 text-[13px] font-semibold text-[#64748B]">{title}</h2> : null}
            <div className="divide-y divide-[#0F2A44]/[0.06] overflow-hidden rounded-[18px] bg-white shadow-sm">{children}</div>
        </section>
    );
}

export interface SettingsRowProps {
    label: ReactNode;
    icon?: IconType;
    /** Muted current value on the end side. */
    value?: ReactNode;
    description?: ReactNode;
    to?: string;
    onClick?: () => void;
    /** Boolean row: renders a switch instead of a chevron. */
    checked?: boolean;
    onCheckedChange?: (checked: boolean) => void;
    destructive?: boolean;
    disabled?: boolean;
    /** Trailing override (e.g. a lock icon or a native <select>). */
    trailing?: ReactNode;
    badge?: ReactNode;
    /** Mirror a directional icon (e.g. LogOut) under RTL. */
    iconFlipRtl?: boolean;
    testId?: string;
}

export function SettingsRow({ label, icon: Icon, value, description, to, onClick, checked, onCheckedChange, destructive, disabled, trailing, badge, iconFlipRtl, testId }: SettingsRowProps) {
    const isSwitch = onCheckedChange !== undefined;
    const labelId = useId();
    const cls = cn(
        'flex min-h-14 w-full items-center gap-3 px-4 py-2.5 text-start disabled:opacity-50',
        (to || onClick || isSwitch) && `transition-colors hover:bg-[#0F2A44]/[0.03] ${focusRing} focus-visible:ring-inset focus-visible:ring-offset-0`,
    );
    const body = (
        <>
            {Icon ? (
                <span className={cn('grid h-9 w-9 shrink-0 place-items-center rounded-full', destructive ? 'bg-[#EF4444]/10 text-[#B91C1C]' : 'bg-[#1FB7A6]/10 text-[#146F66]')}>
                    <Icon className={cn('h-[18px] w-[18px]', iconFlipRtl && 'rtl:rotate-180')} aria-hidden="true" />
                </span>
            ) : null}
            <span className="min-w-0 flex-1">
                <span id={labelId} className={cn('block truncate text-[15px] font-medium', destructive ? 'text-[#B91C1C]' : 'text-[#0F2A44]')}>{label}</span>
                {description ? <span className="block truncate text-[13px] text-[#64748B]">{description}</span> : null}
            </span>
            {badge}
            {value !== undefined ? <span className="max-w-[40%] shrink-0 truncate text-[14px] text-[#64748B]">{value}</span> : null}
            {trailing ?? (isSwitch ? (
                <span aria-hidden="true" className={cn('relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors', checked ? 'bg-[#1FB7A6]' : 'bg-[#CBD5E1]')}>
                    <span className={cn('absolute h-5 w-5 rounded-full bg-white shadow transition-[inset-inline-start]', checked ? 'start-6' : 'start-1')} />
                </span>
            ) : (to || onClick) && !destructive ? (
                <ChevronRight className="h-5 w-5 shrink-0 text-[#94A3B8] rtl:rotate-180" aria-hidden="true" />
            ) : null)}
        </>
    );
    if (isSwitch) {
        return (
            <button type="button" role="switch" aria-checked={Boolean(checked)} aria-labelledby={labelId} disabled={disabled} onClick={() => onCheckedChange!(!checked)} className={cls} data-testid={testId}>
                {body}
            </button>
        );
    }
    if (to && !disabled) return <Link to={to} className={cls} data-testid={testId}>{body}</Link>;
    if (onClick) return <button type="button" onClick={onClick} disabled={disabled} className={cls} data-testid={testId}>{body}</button>;
    return <div className={cls} data-testid={testId}>{body}</div>;
}

/* ─────────────────────────── Export row ─────────────────────────── */

export interface ExportActionRowProps {
    onDownload?: () => void;
    onPrintPdf?: () => void;
    onExcel?: () => void;
    onShare?: () => void;
    disabled?: boolean;
}

/** Download / Print PDF / Excel / Share. Actions without a handler are not rendered. */
export function ExportActionRow({ onDownload, onPrintPdf, onExcel, onShare, disabled }: ExportActionRowProps) {
    const { t } = useTranslation();
    const actions = [
        { key: 'download', icon: Download, label: t('mobile.export.download', 'Download'), handler: onDownload },
        { key: 'pdf', icon: Printer, label: t('mobile.export.printPdf', 'Print PDF'), handler: onPrintPdf },
        { key: 'excel', icon: FileSpreadsheet, label: t('mobile.export.excel', 'Excel'), handler: onExcel },
        { key: 'share', icon: Share2, label: t('mobile.export.share', 'Share'), handler: onShare },
    ].filter((action) => typeof action.handler === 'function');
    if (actions.length === 0) return null;
    return (
        <div className="flex justify-around gap-2 rounded-[18px] bg-white p-3 shadow-sm" data-testid="mobile-export-row">
            {actions.map(({ key, icon: Icon, label, handler }) => (
                <button key={key} type="button" onClick={handler} disabled={disabled} className={`flex min-h-11 min-w-16 flex-col items-center gap-1 rounded-xl p-1 text-[12px] font-medium text-[#0F2A44] disabled:opacity-50 ${focusRing}`}>
                    <span className="grid h-11 w-11 place-items-center rounded-full bg-[#1FB7A6]/10 text-[#146F66]"><Icon className="h-5 w-5" aria-hidden="true" /></span>
                    {label}
                </button>
            ))}
        </div>
    );
}
